/**
 * Runtime Application Configuration
 *
 * Fetches public configuration from the backend at app startup.
 * This replaces build-time VITE_* env vars, making the Docker image portable.
 */

export interface AppConfig {
    mockAuth: boolean
    needsSetup: boolean
    azureAdClientId: string
    azureAdTenantId: string
}

let _config: AppConfig | null = null

/**
 * Fetch configuration from the backend. Must be called once before React renders.
 * Uses plain `fetch` to avoid circular dependency with the axios http-client.
 */
export async function loadAppConfig(): Promise<AppConfig> {
    const res = await fetch('/api/config')
    if (!res.ok) {
        throw new Error(`Failed to load app config: ${res.status}`)
    }

    const data = await res.json()

    _config = {
        mockAuth: data.mock_auth ?? false,
        needsSetup: data.needs_setup ?? false,
        azureAdClientId: data.azure_ad_client_id ?? '',
        azureAdTenantId: data.azure_ad_tenant_id ?? '',
    }

    return _config
}

/**
 * Synchronous getter for the cached config.
 * Throws if called before `loadAppConfig()` has resolved.
 */
export function getAppConfig(): AppConfig {
    if (!_config) {
        throw new Error('App config not loaded yet — call loadAppConfig() first')
    }
    return _config
}
